'use client';
import { Skeleton } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import type { ComponentProps } from 'react';
import { ResultsSectionLink } from '..';
import { ContextualNavigation } from '../../../../ContextualNavigation/ContextualNavigation';
import { LotteryResult } from '../../../../lottery-results/components/LotteryResult/LotteryResult';
import { useResults, type ResultData } from './hooks';

type ResultsSectionResultProps = ComponentProps<'div'> & {
  data: ResultData;
};

/**
 * Component to render the skeleton for the results section result.
 *
 * @param className - (Optional) class name to apply to the component.
 * @returns The skeleton component.
 */
function ResultsSectionResultSkeleton({ className, ...props }: Omit<ResultsSectionResultProps, 'data' | 'data-testid'>) {
  return (
    <div data-testid="results-section-result-skeleton" className={twMerge('flex flex-col gap-4', className)} {...props}>
      <div className="flex gap-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-8 w-full rounded-full" />
        ))}
      </div>
      <Skeleton className="h-12.75 w-full rounded-full" />
      <Skeleton className="h-5 w-40 rounded-md" />
    </div>
  );
}

/**
 * Component to render the result for results section.
 * It renders the contextual navigation, lottery result and results section link.
 *
 * @param data - The data to process by useResults hook and render the result.
 * @param className - (Optional) class name to apply to the component.
 * @returns The result component.
 */
function ResultsSectionResult({ data, className, ...props }: ResultsSectionResultProps) {
  const { result, options, selectedOption, hasExtraGames, handleOptionChange, lotteryId } = useResults(data);

  return (
    <div className={twMerge('flex flex-col gap-4', className)} {...props}>
      {hasExtraGames ? <ContextualNavigation options={options} onChange={handleOptionChange} value={selectedOption} size="s" data-track-container="Navigation" /> : null}
      <LotteryResult result={result} lotteryName={lotteryId} data-track-name="Result" />
      <ResultsSectionLink lottery={lotteryId} data-track-name="Prize Breakdown" />
    </div>
  );
}

export { ResultsSectionResult, ResultsSectionResultSkeleton };
